import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://kqeavasqqhslvphmeshm.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtxZWF2YXNxcWhzbHZwaG1lc2htIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxMzYwMjcsImV4cCI6MjA2NzcxMjAyN30.W7v5BimGfv91fR9Pk9r6RsWPdPv2O6a9EtBllRPslME';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

console.log('🔗 Initializing Supabase client...');

// 創建 Supabase 客戶端
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  auth: {
    persistSession: false,
    autoRefreshToken: false
  }
});

// 清理舊訊息的函數
const cleanupOldMessages = async () => {
  try {
    console.log('🧹 Cleaning up old messages...');
    
    // 刪除24小時前的訊息
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    const { data, error } = await supabase
      .from('messages')
      .delete()
      .lt('created_at', twentyFourHoursAgo);

    if (error) {
      console.error('❌ Error cleaning up old messages:', error);
    } else {
      console.log('✅ Old messages cleaned up successfully');
    }
  } catch (error) {
    console.error('❌ Error in cleanup function:', error);
  }
};

// 清理過期用戶的函數
const cleanupExpiredUsers = async () => {
  try {
    console.log('🧹 Cleaning up expired users...');
    
    // 刪除15分鐘前無活動的用戶
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
    
    const { data, error } = await supabase
      .from('online_users')
      .delete()
      .lt('last_activity', fifteenMinutesAgo);

    if (error) {
      console.error('❌ Error cleaning up expired users:', error);
    } else {
      console.log('✅ Expired users cleaned up successfully');
    }
  } catch (error) {
    console.error('❌ Error in expired users cleanup:', error);
  }
};

// 清理問題數據的函數
const cleanupProblematicData = async () => {
  try {
    console.log('🧹 Cleaning up problematic data...');
    
    // 刪除問題用戶和訊息
    const problematicNicknames = ['Test User', '你好'];
    
    for (const nickname of problematicNicknames) {
      await supabase
        .from('messages')
        .delete()
        .eq('nickname', nickname);
        
      await supabase
        .from('online_users')
        .delete()
        .eq('nickname', nickname);
    }
    
    console.log('✅ Problematic data cleaned up successfully');
  } catch (error) {
    console.error('❌ Error cleaning up problematic data:', error);
  }
};

// 測試連接和表結構
const testConnection = async () => {
  try {
    console.log('🔍 Testing Supabase connection...');
    
    // 清理問題數據
    await cleanupProblematicData();
    
    // 測試 messages 表
    const { data: messagesData, error: messagesError } = await supabase
      .from('messages')
      .select('count', { count: 'exact', head: true });

    if (messagesError) {
      console.error('❌ Messages table error:', messagesError);
    } else {
      console.log('✅ Messages table connected successfully! Count:', messagesData);
    }

    // 測試 online_users 表
    const { data: usersData, error: usersError } = await supabase
      .from('online_users')
      .select('count', { count: 'exact', head: true });

    if (usersError) {
      console.error('❌ Online users table error:', usersError);
    } else {
      console.log('✅ Online users table connected successfully! Count:', usersData);
    }
    
    // 測試插入一個用戶來驗證權限和表結構
    const testUser = {
      id: 'test-connection-' + Date.now(),
      nickname: 'Connection Test',
      connected_at: new Date().toISOString(),
      last_activity: new Date().toISOString()
    };
    
    console.log('🧪 Testing user insert with data:', testUser);
    
    const { data: insertData, error: insertError } = await supabase
      .from('online_users')
      .insert([testUser])
      .select();
    
    if (insertError) {
      console.error('❌ Insert test failed:', insertError);
      console.error('Error details:', insertError.message, insertError.details, insertError.hint);
    } else {
      console.log('✅ Insert test successful:', insertData);
      
      // 立即刪除測試用戶
      const { error: deleteError } = await supabase
        .from('online_users')
        .delete()
        .eq('id', testUser.id);
      
      if (deleteError) {
        console.error('❌ Test user cleanup failed:', deleteError);
      } else {
        console.log('✅ Test user cleaned up');
      }
    }
    
    // 執行舊訊息清理
    await cleanupOldMessages();
    
    // 執行過期用戶清理
    await cleanupExpiredUsers();
    
  } catch (error) {
    console.error('❌ Connection test failed:', error);
  }
};

// 設置定期清理任務
const setupPeriodicCleanup = () => {
  console.log('⏰ Setting up periodic cleanup tasks...');
  
  // 每小時清理一次舊訊息
  setInterval(cleanupOldMessages, 60 * 60 * 1000);
  
  // 每5分鐘清理一次過期用戶
  setInterval(cleanupExpiredUsers, 5 * 60 * 1000);
  
  // 每10分鐘清理一次問題數據
  setInterval(cleanupProblematicData, 10 * 60 * 1000);
  
  console.log('✅ Periodic cleanup tasks set up successfully');
};

// 執行連接測試和設置清理任務
testConnection();
setupPeriodicCleanup();

console.log('✅ Supabase client initialized successfully');

export { supabase };